import { Auth, getClientIdWithIdentifier, identifyClient } from '@nackle/bridge';
import { idURL } from 'endpoints';
import { CLIENT_ID, APP_STORAGE_KEY, USER_STORAGE_KEY } from '../constants';
import { message } from '@nackle/paper';
import { getString } from '@nackle/intl-tools';

function getAuth( appCode ) {
    const clientId = getClientIdWithIdentifier( CLIENT_ID, appCode );
    return new Auth( {
        authority: idURL,
        clientId: clientId,
        offlineAccess: true,
        monitorAccessTokenExpiration: false,
        popup: false
    } );
}

export function isInAuthCallback( code ) {
    // appcode in dialog for safari is present in sessionStorage so we either accept that or take it from localStorage
    const appCode = code || localStorage.getItem( APP_STORAGE_KEY );
    const auth = getAuth( appCode );
    if ( auth.isAuthUri() ) {
        auth.resume();
        return true;
    }
    return false;
}

export async function getUser( refreshToken = '' ) {
    const appCode = localStorage.getItem( APP_STORAGE_KEY );
    // see if the user is in local storage
    let existingUser = localStorage.getItem( USER_STORAGE_KEY );

    if ( existingUser ) {
        existingUser = JSON.parse( existingUser );
    }
    // if the user exists and the token hasn't expired; just return
    if ( existingUser && new Date() < new Date( existingUser.access_token_expires_at ) ) {
        return existingUser;
    }
    // if the user exists; try to refresh the token
    if ( existingUser ) {
        console.log( 'Getting refresh Token' );
        try {
            const res = await fetch( `${ idURL }/token`,
                {
                    'method': 'POST',
                    'headers': {
                        'content-type': 'application/x-www-form-urlencoded'
                    },
                    'body': [
                        'grant_type=refresh_token',
                        `client_id=${ encodeURIComponent( getClientIdWithIdentifier( CLIENT_ID, appCode ) ) }`,
                        `refresh_token=${ encodeURIComponent( ( refreshToken && refreshToken !== existingUser.refresh_token ) ? refreshToken : existingUser.refresh_token ) }`
                    ].join( '&' )
                } );
            const json = await res.json();
            const { access_token, expires_in } = json;
            const newExpire = await getTokenExpire( expires_in );
            const userInfo = await getUserInfo( access_token );

            const user = { ...json, access_token_expires_at: newExpire, claims: userInfo, client_id: getClientIdWithIdentifier( CLIENT_ID, appCode ) };
            localStorage.setItem( USER_STORAGE_KEY, JSON.stringify( user ) );
            return user;
        } catch ( err ) {
            message.error( getString( 'recognition.error.generic', { error: 'Something went wrong with authentication' } ) );
            return;
        }
    }
    // there was no existing user; or refreshing failed
    return null;
}

export async function testAppCode( appCode ) {
    const { success } = await identifyClient(
        idURL,
        getClientIdWithIdentifier( CLIENT_ID, appCode )
    );
    return success;
}

export async function signIn( appCode ) {
    const auth = getAuth( appCode );
    await new Promise( ( resolve, reject ) => {
        auth.events.signIn.on( resolve );
        auth.events.signInError.on( resolve );
        auth.signIn();
    } );
    // set the existing user
    const user = auth.state;
    // save the user in storage
    localStorage.setItem( USER_STORAGE_KEY, JSON.stringify( user ) );
    return user;
}

const getTokenExpire = ( expires_in ) => {
    const newExpire = new Date();
    newExpire.setSeconds( newExpire.getSeconds() + expires_in );
    return newExpire;
};

const getUserInfo = async ( access_token ) => {
    const meResp = await fetch( `${ idURL }/me`,
        {
            'method': 'POST',
            'headers': {
                'content-type': 'application/x-www-form-urlencoded',
                'authorization': `Bearer ${ access_token }`
            }
        } );
    return meResp.json();
};

export async function signOut() {
    localStorage.removeItem( USER_STORAGE_KEY );
    localStorage.removeItem( APP_STORAGE_KEY );
}